import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Clock, TrendingUp, X, Loader2, Star, ShoppingCart, Mic, MicOff, Flame, ArrowUpRight, Trash2 } from 'lucide-react';
import { searchApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const EnhancedSearch = ({ onProductSelect, onAddToCart, onSearch, onCategorySelect }) => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState({ products: [], categories: [], brands: [] });
  const [recentSearches, setRecentSearches] = useState([]);
  const [trending, setTrending] = useState([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  // Setup voice recognition
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const recognition = new SR();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.onresult = (event) => {
        const text = event.results[event.results.length - 1][0].transcript;
        setQuery(text);
        if (event.results[event.results.length - 1].isFinal) {
          setIsListening(false);
        }
      };
      recognition.onerror = () => setIsListening(false);
      recognition.onend = () => setIsListening(false);
      recognitionRef.current = recognition;
    }
    return () => { try { recognitionRef.current?.abort(); } catch {} };
  }, []);

  const toggleVoice = () => {
    if (!recognitionRef.current) return;
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setQuery('');
      recognitionRef.current.start();
      setIsListening(true);
      setIsOpen(true);
    }
  };

  // Debounce helper
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Fetch autocomplete from API
  const fetchSuggestions = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.length < 2) {
        setSuggestions({ products: [], categories: [], brands: [] });
        return;
      }
      setLoading(true);
      try {
        const data = await searchApi.autocomplete(searchQuery, 8);
        setSuggestions(data);
      } catch (error) {
        console.error('Autocomplete error:', error);
        setSuggestions({ products: [], categories: [], brands: [] });
      } finally {
        setLoading(false);
      }
    }, 250),
    []
  );

  // Load trending and recent on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load from localStorage
        const saved = JSON.parse(localStorage.getItem('medicare_recent_searches') || '[]');
        setRecentSearches(saved.slice(0, 5));

        // Fetch trending from API
        const trendingData = await searchApi.getTrending(5);
        setTrending(trendingData.trending || []);
      } catch (error) {
        console.error('Failed to load search data:', error);
      }
    };
    loadInitialData();
  }, []);

  // Fetch suggestions when query changes
  useEffect(() => {
    fetchSuggestions(query);
  }, [query, fetchSuggestions]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = async (searchTerm) => {
    if (!searchTerm?.trim()) return;
    
    // Save to recent searches
    const updated = [searchTerm, ...recentSearches.filter(s => s !== searchTerm)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('medicare_recent_searches', JSON.stringify(updated));
    
    // Save to backend if logged in
    if (user?.id) {
      try {
        await searchApi.saveHistory(user.id, searchTerm);
      } catch (e) {
        // Ignore errors
      }
    }
    
    setQuery(searchTerm);
    setIsOpen(false);
    // Trigger full search results view
    onSearch?.(searchTerm);
  };

  const handleProductClick = (product) => {
    handleSearch(product.name);
    onProductSelect?.(product);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('medicare_recent_searches');
    if (user?.id) {
      searchApi.clearHistory(user.id).catch(() => {});
    }
  };

  const removeRecentSearch = (searchTerm) => {
    const updated = recentSearches.filter(s => s !== searchTerm);
    setRecentSearches(updated);
    localStorage.setItem('medicare_recent_searches', JSON.stringify(updated));
  };

  const hasResults = suggestions.products?.length > 0 || suggestions.categories?.length > 0 || suggestions.brands?.length > 0;

  return (
    <div ref={containerRef} className="relative flex-1 max-w-2xl">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch(query)}
          placeholder="Search for medicines, vitamins, supplements..."
          className="w-full pl-4 pr-12 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:border-teal-500 focus:outline-none transition-colors"
          aria-label="Search products"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />
        <button 
          onClick={() => handleSearch(query)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          aria-label="Search"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
        </button>
        {recognitionRef.current && (
          <button
            onClick={toggleVoice}
            data-testid="voice-search-btn"
            className={`absolute right-14 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-all ${
              isListening
                ? 'bg-red-100 dark:bg-red-900/30 text-red-600 animate-pulse'
                : 'text-gray-400 hover:text-teal-600 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            aria-label={isListening ? 'Stop voice search' : 'Start voice search'}
          >
            {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden z-50 max-h-[70vh] overflow-y-auto">
          {/* Product Results */}
          {hasResults && query.length >= 2 && (
            <div className="p-2">
              {suggestions.products?.length > 0 && (
                <>
                  <div className="flex items-center justify-between px-3 py-1">
                    <p className="text-xs font-medium text-gray-500 uppercase">Products</p>
                    {suggestions.fuzzy && (
                      <span
                        data-testid="search-fuzzy-hint"
                        className="text-[10px] font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-full"
                        title="Approximate matches — no exact result found"
                        aria-label="Showing approximate matches because no exact product matched your search"
                      >
                        Did you mean?
                      </span>
                    )}
                  </div>
                  {suggestions.products.map((product, idx) => (
                    <button
                      key={product.id || idx}
                      data-testid={`search-suggest-product-${product.slug || product.id || idx}`}
                      onClick={() => handleProductClick(product)}
                      className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors group"
                    >
                      {product.image ? (
                        <img
                          src={product.image}
                          alt=""
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                          <Search className="w-5 h-5 text-gray-300" />
                        </div>
                      )}
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium text-gray-800 dark:text-white line-clamp-1">{product.name}</p>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-bold text-teal-600 text-sm">
                            ${(product.sale_price || product.price || 0).toFixed?.(2) ?? product.price}
                          </span>
                          {product.brand && (
                            <span className="text-gray-400 truncate">{product.brand}</span>
                          )}
                          {(product.stock_quantity ?? 1) <= 0 && (
                            <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded text-[10px] font-semibold">
                              Out of stock
                            </span>
                          )}
                        </div>
                      </div>
                      {onAddToCart && (product.stock_quantity ?? 1) > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddToCart(product);
                          }}
                          className="p-2 opacity-0 group-hover:opacity-100 bg-teal-100 dark:bg-teal-900 text-teal-600 rounded-lg transition-opacity"
                          aria-label="Add to cart"
                        >
                          <ShoppingCart className="w-4 h-4" />
                        </button>
                      )}
                    </button>
                  ))}
                </>
              )}

              {/* Categories */}
              {suggestions.categories?.length > 0 && (
                <div className="mt-2">
                  <p className="px-3 py-1 text-xs font-medium text-gray-500 uppercase">Categories</p>
                  <div className="flex flex-wrap gap-2 px-3 py-2">
                    {suggestions.categories.map((cat, idx) => {
                      // Backwards-compat: legacy responses returned plain strings
                      const isObj = cat && typeof cat === 'object';
                      const name = isObj ? cat.name : cat;
                      const id = isObj ? cat.id : null;
                      const slug = isObj ? cat.slug : null;
                      return (
                        <button
                          key={id || idx}
                          data-testid={`search-suggest-category-${slug || idx}`}
                          onClick={() => {
                            if (isObj && (id || slug) && onCategorySelect) {
                              onCategorySelect(id, slug);
                              setIsOpen(false);
                            } else {
                              handleSearch(name);
                            }
                          }}
                          className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded-full hover:bg-blue-200 dark:hover:bg-blue-900 transition-colors"
                        >
                          {name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Brands */}
              {suggestions.brands?.length > 0 && (
                <div className="mt-2">
                  <p className="px-3 py-1 text-xs font-medium text-gray-500 uppercase">Brands</p>
                  <div className="flex flex-wrap gap-2 px-3 py-2">
                    {suggestions.brands.map((brand, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSearch(brand)}
                        className="px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm rounded-full hover:bg-purple-200 dark:hover:bg-purple-900 transition-colors"
                      >
                        {brand}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No results message */}
          {query.length >= 2 && !loading && !hasResults && (
            <div className="p-4 text-center text-gray-500">
              <p>No products found for "{query}"</p>
              <p className="text-sm mt-1">Try a different search term</p>
            </div>
          )}

          {/* Recent Searches */}
          {query.length < 2 && recentSearches.length > 0 && (
            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between px-3 py-1">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Your Recent Searches
                </p>
                <button
                  onClick={clearRecentSearches}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors flex items-center gap-1"
                  data-testid="clear-recent-searches"
                >
                  <Trash2 className="w-3 h-3" /> Clear all
                </button>
              </div>
              <div className="mt-1 space-y-0.5">
                {recentSearches.map((search, index) => (
                  <div
                    key={index}
                    className="group flex items-center gap-2 px-3 py-2 hover:bg-teal-50 dark:hover:bg-teal-900/10 rounded-lg transition-colors"
                  >
                    <Clock className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                    <button
                      onClick={() => handleSearch(search)}
                      className="flex-1 text-left text-sm text-gray-700 dark:text-gray-300 hover:text-teal-700 dark:hover:text-teal-400 font-medium"
                      data-testid={`recent-search-${index}`}
                    >
                      {search}
                    </button>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSearch(search); }}
                        className="p-1 text-teal-500 hover:bg-teal-100 dark:hover:bg-teal-900/30 rounded"
                        title="Search again"
                      >
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeRecentSearch(search); }}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Remove"
                        data-testid={`remove-recent-${index}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Popular & Trending Searches */}
          {query.length < 2 && (
            <div className="p-3">
              <p className="px-1 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Flame className="w-3.5 h-3.5 text-orange-500" /> Popular Searches
              </p>
              <div className="flex flex-wrap gap-2">
                {(trending.length > 0 ? trending : [
                  { query: 'Vitamin D', count: 45 }, { query: 'Omega 3', count: 38 }, { query: 'Collagen', count: 32 }, 
                  { query: 'Probiotics', count: 28 }, { query: 'Zinc', count: 24 }, { query: 'Pain Relief', count: 20 },
                  { query: 'First Aid', count: 18 }, { query: 'Thermometer', count: 15 }
                ]).map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleSearch(item.query)}
                    data-testid={`trending-search-${index}`}
                    className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                      index < 3
                        ? 'bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/10 dark:to-amber-900/10 border-orange-200 dark:border-orange-800/40 text-orange-700 dark:text-orange-400 hover:shadow-md hover:shadow-orange-100 dark:hover:shadow-orange-900/20'
                        : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:border-teal-300 dark:hover:border-teal-700 hover:text-teal-700 dark:hover:text-teal-400'
                    }`}
                  >
                    {index < 3 && <Flame className="w-3 h-3 text-orange-400 group-hover:animate-pulse" />}
                    {item.query}
                    {item.count > 0 && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        index < 3
                          ? 'bg-orange-200/60 dark:bg-orange-800/30 text-orange-600 dark:text-orange-400'
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                      }`}>
                        {item.users > 1 ? `${item.users} shoppers` : `${item.count}`}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              {trending.length > 0 && (
                <p className="text-[10px] text-gray-400 mt-2 px-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Based on what shoppers are searching this week
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedSearch;
